<template>
  <Tab :tab="tab">
    <template #header-action>
      <FormulateInput
        type="button"
        :input-class="['btn-primary mb-0 group']"
        :disabled="isSubmitting"
        @click="triggerOnSubmit"
      >
        <span :class="{ hidden: isSubmitting }">Save changes</span>
        <lottie
          :options="LOADING_ANIMATION_OPTIONS"
          :style="{ width: '1.5rem' }"
          :class="{
            'hidden': !isSubmitting,
            'group-hover:hidden': isSubmitting,
          }"
        />
        <lottie
          :options="LOADING_ANIMATION_OPTIONS_DARK"
          :style="{ width: '1.5rem' }"
          :class="{
            'hidden': !isSubmitting,
            'hidden group-hover:block': isSubmitting,
          }"
        />
      </FormulateInput>
    </template>

    <template v-if="settings">
      <FormulateForm v-model="form" @submit="onSubmit">
        <FormulateInput v-show="false" ref="submitInput" type="submit" />

        <div class="grid grid-cols-2 gap-24">
          <div class="col-span-1 flex flex-col gap-12">
            <!-- Details -->
            <div class="w-full flex flex-col gap-6">
              <div>
                <h5 class="m-2">
                  Details
                  <span class="text-xs font-thin italic">(*) required</span>
                </h5>
              </div>

              <div class="grid grid-cols-5 gap-4">
                <div class="col-span-3">
                  <FormulateInput
                    name="schoolName"
                    label="School name (*)"
                    validation="required:trim"
                    validation-name="School name"
                  />
                </div>

                <div class="col-span-2">
                  <FormulateInput
                    type="number"
                    name="audience"
                    label="Number of students (*)"
                    validation="required|number"
                    min="1"
                  />
                </div>
              </div>

              <FormulateInput name="subtitle" label="Subtitle" />

              <div class="w-full grid grid-cols-2">
                <!-- Logo -->
                <div class="col-span-1 flex flex-col relative">
                  <form ref="logoForm">
                    <input
                      ref="logoInput"
                      type="file"
                      class="hidden"
                      @input="selectImage(LOGO_CONFIG_MAP)"
                    />
                  </form>
                  <label for="logoInput" class="block text-sm font-medium">
                    Logo
                  </label>
                  <div class="flex items-center my-1">
                    <div
                      class="banner-changer w-full h-32 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20"
                      :style="{
                        backgroundImage: `url(${logoPreviewUrl})`,
                      }"
                    >
                      <button
                        type="button"
                        class="z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6"
                        @click="updateImage(LOGO_CONFIG_MAP)"
                      >
                        {{ getImageText(LOGO_CONFIG_MAP) }}
                      </button>
                    </div>
                  </div>
                  <div class="text-xs text-truegray-500 mb-4">
                    .jpg or .png format with a maximum file size of 2 MB
                  </div>

                  <FormulateInput
                    v-if="logoPreviewUrl"
                    type="checkbox"
                    name="logoRemove"
                    label="Remove"
                  />
                </div>
                <!-- /Logo -->
              </div>
            </div>
            <!-- /Details -->

            <!-- Address -->
            <div class="w-full flex flex-col gap-6">
              <h5>Address</h5>
              <div class="flex flex-col gap-3">
                <FormulateInput
                  type="text"
                  name="addressLine1"
                  label="Address line 1"
                />
                <FormulateInput
                  type="text"
                  name="addressLine2"
                  label="Address line 2"
                />
                <FormulateInput
                  type="text"
                  name="addressLine3"
                  label="Address line 3"
                />
              </div>
            </div>
            <!-- Address -->
          </div>

          <div class="col-span-1 flex flex-col gap-12">
            <!-- Additional details -->
            <div class="w-full flex flex-col gap-6">
              <h5>Additional</h5>

              <!-- Editor -->
              <FormulateInput
                key="description"
                name="description"
                label="More about us"
                :label-class="['text-truegray-700']"
                :help="`Content in this field will appear as a 'More About Us' button on your home page.`"
                :help-class="['mt-1 px-1 text-truegray-500']"
              >
                <template #element>
                  <Editor
                    :initial-content="initialEditorContent"
                    :active-features="[
                      'headings',
                      'bold',
                      'italic',
                      'strike',
                      'highlight',
                      'text_colour',
                      'underline',
                      'h1',
                      'h2',
                      'h3',
                      'alignments',
                      'alignLeft',
                      'alignCenter',
                      'alignRight',
                      'lists',
                      'bulletList',
                      'orderedList',
                      'blockquote',
                      'code',
                      'codeBlock',
                      'link',
                      'remove_link',
                      'image',
                      'tables',
                      'table',
                      'horizontalRule',
                      'undo',
                      'redo',
                    ]"
                    @update-content="updateEditorContent"
                  />
                </template>
              </FormulateInput>
              <!-- /Editor -->
            </div>
            <!-- Additional details -->

            <!-- Banners -->
            <div class="w-full grid grid-cols-2 gap-4">
              <!-- Hero banner -->
              <div class="col-span-1 flex flex-col relative">
                <form ref="bannerHeroForm">
                  <input
                    ref="bannerHeroInput"
                    type="file"
                    class="hidden"
                    @input="selectImage(BANNER_HERO_CONFIG_MAP)"
                  />
                </form>
                <label for="bannerHero" class="block text-sm font-medium">
                  Home Page Banner
                </label>
                <div class="flex items-center my-1">
                  <div
                    class="banner-changer w-full h-32 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20"
                    :style="{
                      backgroundImage: `url(${bannerHeroPreviewUrl})`,
                    }"
                  >
                    <button
                      type="button"
                      class="z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6"
                      @click="updateImage(BANNER_HERO_CONFIG_MAP)"
                    >
                      {{ getImageText(BANNER_HERO_CONFIG_MAP) }}
                    </button>
                  </div>
                </div>
                <div class="text-xs text-truegray-500 mb-4">
                  .jpg or .png format with a maximum file size of 2 MB
                </div>

                <FormulateInput
                  v-if="bannerHeroPreviewUrl"
                  type="checkbox"
                  name="bannerHeroRemove"
                  label="Reset to default"
                />
              </div>
              <!-- /Hero banner -->

              <!-- Footer banner -->
              <div class="col-span-1 flex flex-col relative">
                <form ref="bannerFooterForm">
                  <input
                    ref="bannerFooterInput"
                    type="file"
                    class="hidden"
                    @input="selectImage(BANNER_FOOTER_CONFIG_MAP)"
                  />
                </form>
                <label for="bannerFooter" class="block text-sm font-medium">
                  Footer Banner
                </label>
                <div class="flex items-center my-1">
                  <div
                    class="banner-changer w-full h-32 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20"
                    :style="{
                      backgroundImage: `url(${bannerFooterPreviewUrl})`,
                    }"
                  >
                    <button
                      type="button"
                      class="z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6"
                      @click="updateImage(BANNER_FOOTER_CONFIG_MAP)"
                    >
                      {{ getImageText(BANNER_FOOTER_CONFIG_MAP) }}
                    </button>
                  </div>
                </div>
                <div class="text-xs text-truegray-500 mb-4">
                  .jpg or .png format with a maximum file size of 2 MB
                </div>

                <FormulateInput
                  v-if="bannerFooterPreviewUrl"
                  type="checkbox"
                  name="bannerFooterRemove"
                  label="Reset to default"
                />
              </div>
              <!-- /Footer banner -->
            </div>
            <!-- /Banners -->

            <!-- WordPress connections -->
            <div class="w-full flex">
              <div v-if="showConnections" class="w-full">
                <h5 class="mb-4">WordPress Connection</h5>
                <div v-if="activeConnectionsStep < 3">
                  <button
                    type="button"
                    :class="
                      activeConnectionsStep === 0
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none absolute'
                    "
                    class="btn btn-trans-black flex items-center space-x-2 m-0"
                    @click="setActiveConnectionsStep(1)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span>Add Your Site</span>
                  </button>

                  <!-- WordPress connection info -->
                  <div
                    :class="
                      activeConnectionsStep === 1
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none absolute'
                    "
                  >
                    <div class="mb-3">
                      <label
                        for="connectionUsername"
                        class="block text-sm font-medium text-gray-700"
                      >
                        Wordpress Site Username
                      </label>
                      <div>
                        <input
                          id="userName"
                          v-model="connectionUsername"
                          type="text"
                          name="userName"
                          autocomplete="User name"
                          :class="INPUT_CLASS"
                          class="w-full"
                          @keyup="validateConnectionStep1"
                        />
                      </div>
                    </div>
                    <div class="mb-3">
                      <label
                        for="connectionSiteUrl"
                        class="block text-sm font-medium text-gray-700"
                      >
                        Wordpress Site URL
                      </label>
                      <div>
                        <input
                          id="siteUrl"
                          v-model="connectionSiteUrl"
                          type="url"
                          name="siteUrl"
                          autocomplete="Site url"
                          :class="INPUT_CLASS"
                          class="w-full"
                          @keyup="validateConnectionStep1"
                        />
                      </div>
                    </div>
                    <div class="mb-3">
                      <label
                        for="connectionSiteKey"
                        class="block text-sm font-medium text-gray-700"
                      >
                        Application Password
                      </label>
                      <div>
                        <input
                          id="siteKey"
                          v-model="connectionSiteKey"
                          type="password"
                          name="siteKey"
                          autocomplete="Site key"
                          :class="INPUT_CLASS"
                          class="w-full"
                          @keyup="validateConnectionStep1"
                        />
                      </div>
                    </div>
                    <div class="mb-3 flex space-x-6">
                      <button
                        :disabled="
                          !canSubmitConnectionsStep1 || connectionsLoading
                        "
                        :class="[
                          !canSubmitConnectionsStep1
                            ? 'opacity-25 cursor-not-allowed'
                            : '',
                        ]"
                        type="button"
                        class="relative"
                        @click="connectWordPress"
                      >
                        <div
                          :class="[
                            !canSubmitConnectionsStep1
                              ? 'border border-truegray-700'
                              : 'btn-black',
                            connectionsLoading
                              ? 'bg-black text-white cursor-not-allowed'
                              : '',
                          ]"
                          class="relative btn mb-0"
                        >
                          <div
                            class="absolute inset-0 w-full flex justify-center items-center z-20"
                          >
                            <lottie
                              v-if="connectionsLoading"
                              :options="LOADING_ANIMATION_OPTIONS"
                            />
                          </div>
                          <span :class="{ invisible: connectionsLoading }">
                            Authorise
                          </span>
                        </div>
                      </button>

                      <button
                        type="button"
                        class="btn btn-expo-red mb-0"
                        @click="setActiveConnectionsStep(0)"
                      >
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                  <!-- Category -->
                  <div
                    :class="
                      activeConnectionsStep === 2
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none absolute'
                    "
                  >
                    <div class="mb-3">
                      <label
                        for="connection_category_id"
                        class="block text-sm font-medium text-gray-700"
                      >
                        Your post category
                      </label>
                      <div>
                        <InputDropdown
                          :placeholder-default="'Select category here'"
                          :selected-options-initial="[connectionCategoryId]"
                          :options="connectionWordPressCategories"
                          :margin-bottom="false"
                          :response-selection-id="'new_category_id'"
                        />
                      </div>
                    </div>
                    <div class="mb-3 flex space-x-6">
                      <!--
                        <button @click="confirmConnection" :disabled="!step2CanSubmit" :class="!step2CanSubmit ? 'opacity-25 cursor-not-allowed': '' " type="submit" class="btn btn-black">
                        <span>Confirm</span>
                        </button> 
                      -->

                      <button
                        :disabled="
                          !canSubmitConnectionsStep1 || connectionsLoading
                        "
                        :class="[
                          !canSubmitConnectionsStep2
                            ? 'opacity-25 cursor-not-allowed'
                            : '',
                        ]"
                        type="button"
                        class="relative"
                        @click="confirmConnection"
                      >
                        <div
                          :class="[
                            !canSubmitConnectionsStep2
                              ? 'border border-truegray-700'
                              : 'btn-black',
                            connectionsLoading
                              ? 'bg-black text-white cursor-not-allowed'
                              : '',
                          ]"
                          class="relative btn mb-0"
                        >
                          <div
                            class="absolute inset-0 mb-0 w-full flex justify-center items-center z-20"
                          >
                            <lottie
                              v-if="connectionsLoading"
                              :options="LOADING_ANIMATION_OPTIONS"
                            />
                          </div>
                          <span :class="{ invisible: connectionsLoading }">
                            Submit
                          </span>
                        </div>
                      </button>

                      <button
                        type="button"
                        class="btn btn-expo-red"
                        @click="cancelConnection"
                      >
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  v-if="activeConnectionsStep === 3"
                  class="flex justify-start items-center w-full space-x-2 bg-gray-50 p-2 rounded border my-4"
                >
                  <div class="flex justify-start w-full space-x-4">
                    <span>
                      Connected site: {{ connectionsWordPress.siteUrl }}
                    </span>
                  </div>

                  <div
                    class="group flex items-center text-gray-300 hover:text-gray-500 cursor-pointer transition-all duration-100"
                    @click="cancelConnection"
                  >
                    <div class="w-24">Remove Site</div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <!-- /WordPress connections -->
          </div>
        </div>
      </FormulateForm>
    </template>
  </Tab>
</template>

<script>
import { defineComponent, ref, computed, onMounted, nextTick, watch } from 'vue'
import { useStore } from '../../../../../js/store'
import Tab from './slots/Tab.vue'
import {
  LOADING_ANIMATION_OPTIONS,
  LOADING_ANIMATION_OPTIONS_DARK,
} from '../../../../../js/util/constants'
import { EventBus } from '../../../../../js/plugins/event-bus'
import Editor from '../../components/editor-wysiwyg/Editor.vue'
import {
  validateImageFormat,
  validateURL,
  validateURLProtocol,
  updateURLWithProtocol,
} from '../../../../../js/util/helper'
import InputDropdown from '../../../../../shared/components/InputDropdown.vue'

export default defineComponent({
  name: 'TabBasics',

  components: {
    Tab,
    Editor,
    InputDropdown,
  },

  props: {
    tab: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const store = useStore()
    const settings = computed(() => store.getters['shared/getSettings'])
    const isSuperAdmin = computed(() => store.getters['shared/isSuperAdmin'])

    const INPUT_CLASS =
      'flex justify-center items-center sm:text-sm text-truegray-700 focus:text-black active:text-black focus:outline-none active:outline-none transition-all duration-100 ease-in-out border border-truegray-300 focus:border-black active:border-black rounded bg-white px-4 h-12'

    const isSubmitting = ref(false)
    const form = ref({
      schoolName: null,
      subtitle: null,
      description: null,
      addressLine1: null,
      addressLine2: null,
      addressLine3: null,
      audience: null,
      logo: null,
      logoRemove: false,
      bannerHero: null,
      bannerHeroRemove: false,
      bannerFooter: null,
      bannerFooterRemove: false,
    })

    // Logo
    const logoForm = ref(null)
    const logoInput = ref(null)
    const logoPreviewUrl = ref(null)
    const logoPreviewChanged = ref(false)

    const LOGO_CONFIG_MAP = {
      hasChanged: logoPreviewChanged,
      formValue: 'logo',
      settingsValue: 'logo',
      formValueRemove: 'logoRemove',
      settingsValueRemove: 'logo_remove',
      previewUrl: logoPreviewUrl,
      inputForm: logoForm,
      inputRef: logoInput,
    }

    // Banner - Hero
    const bannerHeroForm = ref(null)
    const bannerHeroInput = ref(null)
    const bannerHeroPreviewUrl = ref(null)
    const bannerHeroPreviewChanged = ref(false)

    const BANNER_HERO_CONFIG_MAP = {
      hasChanged: bannerHeroPreviewChanged,
      formValue: 'bannerHero',
      settingsValue: 'banner_hero',
      formValueRemove: 'bannerHeroRemove',
      settingsValueRemove: 'banner_hero_remove',
      previewUrl: bannerHeroPreviewUrl,
      inputForm: bannerHeroForm,
      inputRef: bannerHeroInput,
    }

    // Banner - Footer
    const bannerFooterForm = ref(null)
    const bannerFooterInput = ref(null)
    const bannerFooterPreviewUrl = ref(null)
    const bannerFooterPreviewChanged = ref(false)

    const BANNER_FOOTER_CONFIG_MAP = {
      hasChanged: bannerFooterPreviewChanged,
      formValue: 'bannerFooter',
      settingsValue: 'banner_footer',
      formValueRemove: 'bannerFooterRemove',
      settingsValueRemove: 'banner_footer_remove',
      previewUrl: bannerFooterPreviewUrl,
      inputForm: bannerFooterForm,
      inputRef: bannerFooterInput,
    }

    // Image handlers
    const IMAGE_CONFIG_ARR = [
      LOGO_CONFIG_MAP,
      BANNER_HERO_CONFIG_MAP,
      BANNER_FOOTER_CONFIG_MAP,
    ]

    const IMAGE_FORM_KEYS_ARR = IMAGE_CONFIG_ARR.flatMap((config) => [
      config.formValue,
      config.formValueRemove,
    ])

    const updateImage = (config) => {
      if (config.hasChanged.value) {
        // Cancel
        resetImage(config)
      } else {
        // Update
        config.inputRef.value.click()
      }
    }
    const resetImage = (config) => {
      config.hasChanged.value = false
      form.value[config.formValue] = null
      config.previewUrl.value = settings.value[config.settingsValue]
      config.inputForm.value.reset()
    }

    const getImageText = (config) => {
      return config.hasChanged.value ? 'Cancel' : 'Change'
    }

    const selectImage = (config) => {
      const files = config.inputRef.value.files
      const file = files?.[0]
      if (file) {
        if (!validateImageFormat(file)) {
          return
        }
      }

      const reader = new FileReader()
      form.value[config.formValue] = file

      reader.onload = (e) => {
        config.hasChanged.value = true
        form.value[config.formValueRemove] = false
        config.previewUrl.value = e.target.result
      }

      reader.readAsDataURL(file)
    }

    // Editor/description
    const initialEditorContent = computed(
      () => settings.value.profile_description,
    )
    const updateEditorContent = (val) => {
      form.value.description = val
    }

    // WordPress (connections)
    // const connectionsForm = ref({})
    const connections = computed(
      () => store.getters['careerwise/getConnections'],
    )
    const hasConnections = computed(() => settings.value.connections_available)
    const showConnections = computed(
      () => isSuperAdmin.value && hasConnections.value,
    )
    const activeConnectionsStep = ref(0)
    const connectionsLoading = ref(false)
    const connectionUsername = ref(null)
    const connectionSiteUrl = ref(null)
    const connectionSiteKey = ref(null)
    const connectionCategoryId = ref(null)
    const connectionsWordPress = ref({})
    const connectionWordPressCategories = ref([])
    const connectionsURLValid = ref(false)

    const canSubmitConnectionsStep1 = computed(() => {
      return true
    })
    const canSubmitConnectionsStep2 = computed(() => {
      return true
    })

    const setActiveConnectionsStep = (val) => {
      activeConnectionsStep.value = val
    }

    const validateConnectionStep1 = () => {
      let canSubmit = true

      if (connectionUsername.value?.length <= 1) {
        canSubmit = false
      }

      if (!connectionsURLValid.value) {
        canSubmit = false
      }

      if (connectionSiteUrl.value?.length <= 5) {
        canSubmit = false
      }

      if (canSubmit) {
        canSubmitConnectionsStep1.value = true
      }
    }

    const confirmConnection = () => {
      connectionsLoading.value = true

      if (connectionSiteUrl.value.slice(-1) !== '/') {
        connectionSiteUrl.value = connectionSiteUrl.value + '/'
      }
      const payload = {
        site_key: connectionSiteKey.value,
        user_name: connectionUsername.value,
        site_url: connectionSiteUrl.value,
        post_type: 'posts',
        category_id: connectionCategoryId.value,
      }

      axios
        .post('/api/auth/save-wordpress-connection', payload)
        .then((response) => {
          if (response.data.success) {
            connectionsWordPress.value = response.data.connections.wordpress
          }

          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }

          connectionsLoading.value = false
        })
    }

    const connectWordPress = () => {
      connectionsLoading.value = true

      if (connectionSiteUrl.value.slice(-1) !== '/') {
        connectionSiteUrl.value = connectionSiteUrl.value + '/'
      }

      const payload = {
        site_key: connectionSiteKey.value,
        user_name: connectionUsername.value,
        site_url: connectionSiteUrl.value,
      }

      axios
        .post('/api/auth/get-settings-categories', payload)
        .then((response) => {
          if (response.data.success) {
            const categories = response.data.categories
            const categoryOptions = categories.map((category) => {
              return {
                value: category.id,
                text: category.name,
              }
            })

            connectionWordPressCategories.value = categoryOptions
            activeConnectionsStep.value = 2
          }

          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }

          connectionsLoading.value = false
        })
        .catch(() => {
          connectionsLoading.value = false
          EventBus.$emit('triggerAlertResponse', {
            status: 'error',
            message:
              'Credentials rejected by the settings. Please check your details and try again.',
          })
        })

      connectionsWordPress.value.postType = 'posts'
    }

    const cancelConnection = () => {
      axios.get('/api/auth/delete-wordpress-connection').then((response) => {
        if (response.data.success) {
          EventBus.$emit('reloadWebsiteSettings')
          activeConnectionsStep.value = 0
        }

        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }

        connectionsLoading.value = false
      })
    }

    watch(connections, (val) => {
      if (val.wordpress) {
        connectionsWordPress.value = val.wordpress
      }
    })

    watch(connectionsWordPress, (val) => {
      if (val) {
        if (val.siteUrl && val.siteKey && val.userName && !val.categoryId) {
          setActiveConnectionsStep(2)
        } else if (val.categoryId) {
          setActiveConnectionsStep(3)
        }
      }
    })

    watch(connectionSiteUrl, (val) => {
      if (!validateURL(val)) {
        connectionsURLValid.value = false
      } else if (!validateURLProtocol(val)) {
        connectionSiteUrl.value = updateURLWithProtocol(val)
        connectionsURLValid.value = true
      }
    })

    const FORM_MAP = {
      schoolName: 'profile_title',
      subtitle: 'profile_sub_title',
      description: 'profile_description',
      addressLine1: 'address_line1',
      addressLine2: 'address_line2',
      addressLine3: 'address_line3',
      audience: 'audience',
    }

    const mapForm = () => {
      Object.keys(FORM_MAP).forEach((key) => {
        const value = settings.value[FORM_MAP[key]]

        if (key === 'audience') {
          form.value[key] = parseInt(value)
        } else {
          form.value[key] = value
        }
      })

      IMAGE_CONFIG_ARR.forEach((map) => {
        resetImage(map)
      })

      if (settings.value.connections?.wordpress) {
        connectionsWordPress.value = settings.value.connections.wordpress
      }
    }

    const payload = computed(() => {
      const data = new FormData()

      // Form inputs
      Object.keys(form.value).forEach((key) => {
        if (!IMAGE_FORM_KEYS_ARR.includes(key)) {
          // Exclude keys in IMAGE_CONFIG_ARR
          const KEY = FORM_MAP[key] || key
          const value = form.value[key]

          if (value !== undefined && value !== null) {
            data.append(KEY, value)
          }
        }
      })

      // Custom inputs - image handling
      IMAGE_CONFIG_ARR.forEach((map) => {
        if (form.value[map.formValue]) {
          data.append(map.settingsValue, form.value[map.formValue])
        }

        data.append(map.settingsValueRemove, form.value[map.formValueRemove])
      })

      return data
    })

    const submitInput = ref(null)
    const triggerOnSubmit = () => {
      nextTick(() => {
        if (submitInput.value) {
          const nativeSubmitElement = submitInput.value.$el.querySelector(
            'button[type="submit"]',
          )

          if (nativeSubmitElement) {
            nativeSubmitElement.click()
          }
        }
      })
    }

    const onSubmit = async () => {
      isSubmitting.value = true
      await store.dispatch('shared/dispatchUpdateSettings', payload.value)
      isSubmitting.value = false
    }

    const init = async () => {
      mapForm()
      await nextTick()
    }

    onMounted(() => {
      init()

      EventBus.$on('new_category_id', (option) => {
        connectionCategoryId.value = option
      })
    })

    return {
      INPUT_CLASS,
      LOADING_ANIMATION_OPTIONS,
      LOADING_ANIMATION_OPTIONS_DARK,
      isSubmitting,

      settings,
      form,

      logoForm,
      logoInput,
      logoPreviewUrl,
      logoPreviewChanged,
      LOGO_CONFIG_MAP,

      bannerHeroForm,
      bannerHeroInput,
      bannerHeroPreviewUrl,
      bannerHeroPreviewChanged,
      BANNER_HERO_CONFIG_MAP,

      bannerFooterForm,
      bannerFooterInput,
      bannerFooterPreviewUrl,
      bannerFooterPreviewChanged,
      BANNER_FOOTER_CONFIG_MAP,

      updateImage,
      resetImage,
      selectImage,
      getImageText,

      initialEditorContent,
      updateEditorContent,

      showConnections,
      activeConnectionsStep,
      setActiveConnectionsStep,
      connectionsWordPress,
      connectionUsername,
      connectionSiteUrl,
      connectionSiteKey,
      connectionCategoryId,
      connectionWordPressCategories,
      validateConnectionStep1,
      canSubmitConnectionsStep1,
      canSubmitConnectionsStep2,
      connectionsLoading,
      confirmConnection,
      connectWordPress,
      cancelConnection,

      submitInput,
      triggerOnSubmit,
      onSubmit,
    }
  },
})
</script>

<style scoped>
.banner-changer::after {
  background: #000;
  opacity: 0.6;
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 0.25rem;
}
</style>
